import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Types
export interface Permission {
  module: string;
  actions: string[];
}

export interface Role {
  _id: string;
  name: string;
  description?: string;
  organizationId: string;
  permissions: Permission[];
  isSystem: boolean;
  isActive: boolean;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface Invitation {
  _id: string;
  email: string;
  organizationId: string;
  roleId: string;
  invitedBy: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  expiresAt: string;
  acceptedAt?: string;
  acceptedBy?: string;
  message?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Module {
  name: string;
  label: string;
  description: string;
  actions: string[];
}

export interface Action {
  name: string;
  label: string;
  description: string;
}

export interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
  organizationId: string;
  roleId: string;
  isActive: boolean;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

// Role API
export const roleApi = {
  // Get all roles
  getAll: (params?: { page?: number; limit?: number; search?: string; isActive?: boolean }) =>
    api.get<{ success: boolean; data: { roles: Role[]; pagination: any } }>('/roles', { params }),
  
  // Get single role
  getById: (id: string) => api.get<{ success: boolean; data: Role }>(`/roles/${id}`),
  
  // Create role
  create: (data: Partial<Role>) =>
    api.post<{ success: boolean; data: Role; message: string }>('/roles', data),
  
  // Update role
  update: (id: string, data: Partial<Role>) =>
    api.put<{ success: boolean; data: Role; message: string }>(`/roles/${id}`, data),
  
  // Delete role
  delete: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/roles/${id}`),
  
  // Get permissions metadata
  getPermissionsMeta: () =>
    api.get<{ success: boolean; data: { modules: Module[]; actions: Action[] } }>('/roles/meta/permissions'),
  
  // Get role statistics
  getStats: () => api.get<{ success: boolean; data: any }>('/roles/stats/overview'),
};

// Invitation API
export const invitationApi = {
  // Get all invitations
  getAll: (params?: { page?: number; limit?: number; status?: string; search?: string }) =>
    api.get<{ success: boolean; data: { invitations: Invitation[]; pagination: any } }>('/invitations', { params }),
  
  // Create invitation
  create: (data: { email: string; roleId: string; message?: string }) =>
    api.post<{ success: boolean; data: Invitation; message: string }>('/invitations', data),
  
  // Resend invitation
  resend: (id: string) =>
    api.post<{ success: boolean; message: string; data: any }>(`/invitations/${id}/resend`),
  
  // Cancel invitation
  cancel: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/invitations/${id}`),
  
  // Accept invitation (public — new user)
  accept: (token: string, data: { name: string; password: string }) =>
    api.post<{ success: boolean; message: string; data: any; code?: string }>(`/invitations/accept/${token}`, data),

  // Accept invitation as an existing logged-in user
  acceptAsMember: (token: string) =>
    api.post<{ success: boolean; message: string; data: any }>(`/invitations/accept-as-member/${token}`),

  // Get pending invitations addressed to the current user's email
  getPendingForMe: () =>
    api.get<{ success: boolean; data: Array<{ token: string; organizationName: string; roleName: string; inviterName: string; expiresAt: string; message?: string }> }>('/invitations/pending-for-me'),
  
  // Get invitation details (public)
  getDetails: (token: string) =>
    api.get<{ success: boolean; data: any }>(`/invitations/details/${token}`),
  
  // Get invitation statistics
  getStats: () => api.get<{ success: boolean; data: any }>('/invitations/stats/overview'),
  
  // Cleanup expired invitations
  cleanupExpired: () =>
    api.post<{ success: boolean; message: string; data: any }>('/invitations/cleanup/expired'),
};

// Enhanced User API with role management
export const userManagementApi = {
  // Get all users in organization
  getAll: (params?: { page?: number; limit?: number; search?: string; roleId?: string; isActive?: boolean }) =>
    api.get<{ success: boolean; data: { users: User[]; pagination: any } }>('/users', { params }),
  
  // Get single user
  getById: (id: string) => api.get<{ success: boolean; data: User }>(`/users/${id}`),
  
  // Create user
  create: (data: Partial<User>) =>
    api.post<{ success: boolean; data: User; message: string }>('/users', data),
  
  // Update user
  update: (id: string, data: Partial<User>) =>
    api.put<{ success: boolean; data: User; message: string }>(`/users/${id}`, data),
  
  // Delete user
  delete: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/users/${id}`),
  
  // Reset user password
  resetPassword: (id: string) =>
    api.post<{ success: boolean; message: string }>(`/users/${id}/reset-password`),
  
  // Get user statistics
  getStats: () => api.get<{ success: boolean; data: any }>('/users/stats/overview'),
};

export default api;
